"use client"

import React, { useState, useEffect } from "react"
import { useWaitForTransactionReceipt } from "wagmi"
import { AAVE_V3_BASE_TOKENS } from "../data/tokens"
import { useAaveTransactions, calculateTransactionFee } from "../hooks/useAaveTransactions"
import { useTokenPosition } from "../hooks/useUserPositions"
import { useGasEstimate } from "../hooks/useGasEstimate"
import type { Token } from "../types"

interface ActionTabsProps {
  onTransactionSuccess: (action: string, amount: string, token: string) => void
  selectedToken?: Token
  aaveData?: any[]
}

type TabType = "supply" | "borrow" | "repay" | "withdraw"

export function ActionTabs({ onTransactionSuccess, selectedToken, aaveData }: ActionTabsProps) {
  const [activeTab, setActiveTab] = useState<TabType>("supply")
  const [currentToken, setCurrentToken] = useState<Token>(selectedToken || AAVE_V3_BASE_TOKENS[0])
  const [amount, setAmount] = useState("")
  const [fee, setFee] = useState("0.00")
  const [error, setError] = useState<string | null>(null)
  const [transactionHash, setTransactionHash] = useState<`0x${string}` | undefined>(undefined)
  const [pendingTransaction, setPendingTransaction] = useState<{action: string, amount: string, token: string} | null>(null)

  const { 
    supplyWithApprovalMutation, 
    borrowMutation, 
    repayMutation, 
    withdrawMutation 
  } = useAaveTransactions()
  const { position, isLoading: positionLoading } = useTokenPosition(currentToken.address, aaveData, activeTab === 'withdraw' || activeTab === 'repay')
  const { data: gasEstimate, isLoading: gasLoading } = useGasEstimate()
  
  // Wait for transaction confirmation
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash: transactionHash,
  })

  useEffect(() => {
    if (selectedToken) {
      setCurrentToken(selectedToken)
    }
  }, [selectedToken])

  // Handle transaction confirmation
  useEffect(() => {
    if (isConfirmed && pendingTransaction) {
      console.log('[ActionTabs] Transaction confirmed, showing success modal')
      onTransactionSuccess(pendingTransaction.action, pendingTransaction.amount, pendingTransaction.token)
      setPendingTransaction(null)
      setTransactionHash(undefined)
    }
  }, [isConfirmed, pendingTransaction, onTransactionSuccess])

  // Reset amount when switching between action types
  useEffect(() => {
    setAmount("")
    setFee("0.00")
  }, [activeTab])

  const tabs = [
    { id: "supply", label: "Supply", color: "text-green-500" },
    { id: "borrow", label: "Borrow", color: "text-blue-400" },
    { id: "repay", label: "Repay", color: "text-orange-400" },
    { id: "withdraw", label: "Withdraw", color: "text-purple-400" },
  ]

  const handleTransaction = async () => {
    console.log('[ActionTabs] handleTransaction called with amount:', amount, 'activeTab:', activeTab)
    if (!amount || Number.parseFloat(amount) <= 0) {
      console.log('[ActionTabs] No amount or amount <= 0, returning early')
      return
    }

    setError(null)

    try {
      const transactionParams = {
        token: currentToken,
        amount: amount,
      }
      console.log('[ActionTabs] Transaction params:', transactionParams)

      let result
      switch (activeTab) {
        case 'supply':
          console.log('[ActionTabs] Calling supplyWithApprovalMutation.mutateAsync...')
          result = await supplyWithApprovalMutation.mutateAsync(transactionParams)
          console.log('[ActionTabs] Supply result:', result)
          break
        case 'borrow':
          console.log('[ActionTabs] Calling borrowMutation.mutateAsync...')
          result = await borrowMutation.mutateAsync(transactionParams)
          console.log('[ActionTabs] Borrow result:', result)
          break
        case 'repay':
          console.log('[ActionTabs] Calling repayMutation.mutateAsync...')
          result = await repayMutation.mutateAsync(transactionParams)
          console.log('[ActionTabs] Repay result:', result)
          break
        case 'withdraw':
          console.log('[ActionTabs] Calling withdrawMutation.mutateAsync...')
          result = await withdrawMutation.mutateAsync(transactionParams)
          console.log('[ActionTabs] Withdraw result:', result)
          break
        default:
          throw new Error('Invalid action')
      }

      // Check if we got a transaction hash
      if (result && typeof result === 'object' && 'hash' in result && result.hash && result.hash !== 'pending') {
        console.log('[ActionTabs] Transaction submitted with hash:', result.hash)
        // Set the transaction hash to wait for confirmation
        setTransactionHash(result.hash as `0x${string}`)
        setPendingTransaction({
          action: activeTab,
          amount: amount,
          token: currentToken.symbol
        })
        setAmount("")
        setFee("0.00")
        // The success modal will be shown when the transaction is confirmed
      } else {
        // If no hash (Farcaster wallet case), show success after a delay
        // This gives the user time to complete the transaction in their wallet
        console.log('[ActionTabs] No transaction hash returned (Farcaster wallet), showing success after delay')
        setAmount("")
        setFee("0.00")
        
        // Show success modal after a delay to allow transaction completion
        setTimeout(() => {
          onTransactionSuccess(activeTab, amount, currentToken.symbol)
        }, 3000) // 3 second delay
      }
    } catch (err) {
      console.error('Transaction failed:', err)
      
      // Handle specific error types
      let errorMessage = 'Transaction failed'
      
      if (err instanceof Error) {
        if (err.message.includes('cancelled by user') || err.message.includes('UserRejectedRequestError')) {
          errorMessage = 'Transaction was cancelled'
        } else if (err.message.includes('reverted on-chain') || err.message.includes('Transaction failed - it may have reverted')) {
          errorMessage = 'Transaction failed - it may have reverted on-chain. Please check your balance and try again.'
        } else if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient funds for this transaction'
        } else if (err.message.includes('gas')) {
          errorMessage = 'Transaction failed due to gas issues'
        } else {
          errorMessage = err.message
        }
      }
      
      setError(errorMessage)
    }
  }

  const getCurrentMutation = () => {
    switch (activeTab) {
      case 'supply': return supplyWithApprovalMutation
      case 'borrow': return borrowMutation
      case 'repay': return repayMutation
      case 'withdraw': return withdrawMutation
      default: return supplyWithApprovalMutation
    }
  }

  const isLoading = getCurrentMutation().isPending || isConfirming

  const getButtonText = () => {
    if (getCurrentMutation().isPending) return "Confirming..."
    if (isConfirming) return "Waiting for confirmation..."
    switch (activeTab) {
      case "supply":
        return `Supply ${currentToken.symbol}`
      case "borrow":
        return `Borrow ${currentToken.symbol}`
      case "repay":
        return `Repay ${currentToken.symbol}`
      case "withdraw":
        return `Withdraw ${currentToken.symbol}`
    }
  }

  const getBalanceLabel = () => {
    switch (activeTab) {
      case "supply":
        return "Wallet Balance"
      case "borrow":
        return "Available to Borrow"
      case "repay":
        return "Debt Balance"
      case "withdraw":
        return "Supplied Balance"
    }
  }

  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount)
    setError(null) // Clear error when amount changes
    if (newAmount && Number.parseFloat(newAmount) > 0) {
      setFee(calculateTransactionFee(newAmount).toFixed(2))
    } else {
      setFee("0.00")
    }
  }

  const getMaxAmount = () => {
    if (positionLoading || !position) {
      return "0"
    }
    
    switch (activeTab) {
      case "supply":
        // For supply, use wallet balance
        const balancePart = currentToken.balance.split(" ")[0]
        return balancePart.replace(/,/g, '')
      case "borrow":
        // For borrow, use available borrowing power
        return position.availableToBorrow
      case "repay":
        // For repay, use current debt amount
        return position.availableToRepay
      case "withdraw":
        // For withdraw, use supplied amount
        return position.availableToWithdraw
      default:
        return "0"
    }
  }

  const getDisplayAmount = (): string | React.ReactElement => {
    if (positionLoading) {
      return "Loading..."
    }
    
    if (!position) {
      return "0"
    }
    
    switch (activeTab) {
      case "supply":
        // For supply, show wallet balance in both USD and token amounts
        const supplyTokenAmount = currentToken.balance.split(" ")[0].replace(/,/g, '')
        const supplyUsdAmount = parseFloat(supplyTokenAmount) * (currentToken.priceInUsd || 1)
        return (
          <div className="text-right">
            <div className="text-sm">${supplyUsdAmount.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">{supplyTokenAmount} {currentToken.symbol}</div>
          </div>
        )
      case "borrow":
        // For borrow, show both USD and token amounts on separate lines
        const tokenAmount = position.availableToBorrow
        const usdAmount = parseFloat(tokenAmount) * (currentToken.priceInUsd || 1)
        return (
          <div className="text-right">
            <div className="text-sm">${usdAmount.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">{tokenAmount} {currentToken.symbol}</div>
          </div>
        )
      case "repay":
        // For repay, show debt amount in both USD and token amounts
        const repayTokenAmount = position.availableToRepay
        const repayUsdAmount = parseFloat(repayTokenAmount) * (currentToken.priceInUsd || 1)
        return (
          <div className="text-right">
            <div className="text-sm">${repayUsdAmount.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">{repayTokenAmount} {currentToken.symbol}</div>
          </div>
        )
      case "withdraw":
        // For withdraw, show supplied amount in both USD and token amounts
        const withdrawTokenAmount = position.availableToWithdraw
        const withdrawUsdAmount = parseFloat(withdrawTokenAmount) * (currentToken.priceInUsd || 1)
        return (
          <div className="text-right">
            <div className="text-sm">${withdrawUsdAmount.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">{withdrawTokenAmount} {currentToken.symbol}</div>
          </div>
        )
      default:
        return "0"
    }
  }

  return (
    <div className="card">
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-background rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as TabType)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? "bg-card text-card-foreground" : "text-muted-foreground hover:text-card-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Amount Input */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-card-foreground">Amount</label>
          <div className="text-xs text-muted-foreground">
            {getBalanceLabel()}: {getDisplayAmount()}
          </div>
        </div>

        <div className="relative">
          <input
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0.00"
            className="w-full input-field pr-16"
          />
          <button
            onClick={() => handleAmountChange(getMaxAmount())}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-primary hover:text-primary/80"
          >
            MAX
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="text-sm text-destructive font-medium">Transaction Failed</div>
          <div className="text-xs text-destructive/80 mt-1">{error}</div>
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={handleTransaction}
        disabled={!amount || Number.parseFloat(amount) <= 0 || isLoading}
        className="w-full py-4 px-6 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-primary-foreground font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
      >
        {getButtonText()}
      </button>

      {/* Transaction Preview */}
      {amount && Number.parseFloat(amount) > 0 && (
        <div className="mt-4 p-3 bg-muted/20 rounded-lg border border-border">
          <div className="text-xs text-muted-foreground mb-2">Transaction Preview</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Action:</span>
              <span className="capitalize text-card-foreground">{activeTab}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount:</span>
              <span className="text-card-foreground">
                {amount} {currentToken.symbol}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Network:</span>
              <span className="text-card-foreground">Base</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Protocol:</span>
              <span className="text-card-foreground">Aave V3</span>
            </div>
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Platform Fee (0.1%):</span>
                <span className="text-card-foreground">
                  {fee} {currentToken.symbol}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gas Fee:</span>
                <span className="text-card-foreground">
                  {gasLoading ? '...' : `~$${gasEstimate?.gasPriceUSD || '0.10'}`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
